import { useEffect, useMemo, useRef, useState } from "react";
import {
  GiLockedChest,
  GiOpenChest,
  GiSandsOfTime,
  GiSprout,
  GiTreasureMap,
} from "react-icons/gi";
import {
  useClaimChest,
  useClaimQuest,
  useQuests,
  type DailyChest,
  type QuestEntry,
  type QuestReward,
} from "../api/quests";
import { useClaimNewbieChest, useNewbieChest, type NewbieChestState } from "../api/newbieChest";
import { Loading } from "../pages/Loading";
import { ErrorView } from "../pages/ErrorView";
import { CurrencyValue } from "./CurrencyIcon";
import { formatNumber, formatDuration, secondsUntil } from "../lib/format";
import { useNow } from "../lib/ticker";
import { hapticImpact, hapticNotification } from "../lib/telegram";

interface Props {
  enabled: boolean;
}

export function QuestsView({ enabled }: Props) {
  const quests = useQuests(enabled);
  const claim = useClaimQuest();
  const claimChest = useClaimChest();
  const newbieChest = useNewbieChest(enabled);
  const claimNewbieChest = useClaimNewbieChest();

  // Short-lived sparkle after a successful claim. Quest id is the trigger
  // identity so each claim fires its own burst on the right card.
  const [recentClaim, setRecentClaim] = useState<string | null>(null);

  // Refs to claimable items so we can auto-scroll the screen to the first one
  // when the user opens Quests, and to the next one after each claim. Keys
  // match the ids used by recentClaim ("newbie-chest", chest.id, quest.id).
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const setItemRef = (id: string) => (el: HTMLElement | null) => {
    if (el) itemRefs.current.set(id, el);
    else itemRefs.current.delete(id);
  };

  // First claimable item in render order: newbie chest → onboarding quests →
  // daily chests → daily quests. Recomputed whenever the underlying data
  // changes; an effect below scrolls to it.
  const firstClaimableId = useMemo<string | null>(() => {
    const nc = newbieChest.data;
    if (nc && !nc.graduated && nc.available) {
      const due = new Date(nc.nextAvailableAt).getTime() - Date.now();
      if (due <= 0) return "newbie-chest";
    }
    const q = quests.data;
    if (!q) return null;
    const onboarding = q.onboarding.find((x) => x.claimable && !x.claimed);
    if (onboarding) return onboarding.id;
    const dailyChest = q.dailyChests.find((c) => c.claimable && !c.claimed);
    if (dailyChest) return dailyChest.id;
    const daily = q.daily.find((x) => x.claimable && !x.claimed);
    if (daily) return daily.id;
    return null;
  }, [quests.data, newbieChest.data]);

  useEffect(() => {
    if (!firstClaimableId) return;
    // Defer one frame so refs from this render are populated before scrolling.
    const id = window.requestAnimationFrame(() => {
      const el = itemRefs.current.get(firstClaimableId);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [firstClaimableId]);

  if (quests.isPending) return <Loading message="Открываю свиток заданий..." />;
  if (quests.isError) return <ErrorView message="Не получилось загрузить задания." />;
  const data = quests.data!;

  const onboardingActive = data.onboarding.filter((q) => !q.claimed);
  const allOnboardingDone = onboardingActive.length === 0;
  const completedDailyCount = data.daily.filter((q) => q.completed).length;

  const onClaim = (questId: string) => {
    hapticImpact("medium");
    claim.mutate(questId, {
      onSuccess: () => {
        hapticNotification("success");
        setRecentClaim(questId);
        setTimeout(() => setRecentClaim(null), 720);
      },
      onError: () => hapticNotification("error"),
    });
  };

  const onClaimChest = (chestId: string) => {
    hapticImpact("heavy");
    claimChest.mutate(chestId, {
      onSuccess: () => {
        hapticNotification("success");
        setRecentClaim(chestId);
        setTimeout(() => setRecentClaim(null), 720);
      },
      onError: () => hapticNotification("error"),
    });
  };

  const onClaimNewbieChest = () => {
    hapticImpact("heavy");
    claimNewbieChest.mutate(undefined, {
      onSuccess: () => {
        hapticNotification("success");
        setRecentClaim("newbie-chest");
        setTimeout(() => setRecentClaim(null), 720);
      },
      onError: () => hapticNotification("error"),
    });
  };

  return (
    <div className="space-y-3 px-3 pb-3">
      {newbieChest.data && !newbieChest.data.graduated && (
        <Section
          title={<span className="inline-flex items-center gap-1.5"><GiTreasureMap size={14} /> Сундук новичка</span>}
          subtitle={`до Замка Lvl ${newbieChest.data.graduationLevel}`}
        >
          <NewbieChestCard
            state={newbieChest.data}
            onClaim={onClaimNewbieChest}
            claiming={claimNewbieChest.isPending}
            justClaimed={recentClaim === "newbie-chest"}
            cardRef={setItemRef("newbie-chest")}
          />
        </Section>
      )}

      {!allOnboardingDone && (
        <Section
          title={<span className="inline-flex items-center gap-1.5"><GiSprout size={14} /> Задания для новичка</span>}
          subtitle={`${data.onboarding.filter(q => q.claimed).length} из ${data.onboarding.length}`}
        >
          <div className="space-y-2">
            {onboardingActive.map((q) => (
              <QuestCard
                key={q.id}
                quest={q}
                onClaim={() => onClaim(q.id)}
                claiming={claim.isPending}
                justClaimed={recentClaim === q.id}
                cardRef={setItemRef(q.id)}
              />
            ))}
          </div>
        </Section>
      )}

      {data.dailyChests.length > 0 && (
        <Section
          title={<span className="inline-flex items-center gap-1.5"><GiOpenChest size={14} /> Сундуки за задания</span>}
          subtitle={`${completedDailyCount} / ${data.daily.length} выполнено`}
        >
          <div className="grid grid-cols-3 gap-2">
            {data.dailyChests.map((c) => (
              <ChestCard
                key={c.id}
                chest={c}
                onClaim={() => onClaimChest(c.id)}
                claiming={claimChest.isPending}
                justClaimed={recentClaim === c.id}
                cardRef={setItemRef(c.id)}
              />
            ))}
          </div>
        </Section>
      )}

      <Section
        title={<span className="inline-flex items-center gap-1.5"><GiSandsOfTime size={14} /> Ежедневные задания</span>}
        subtitle={<ResetCountdown resetAtUtc={data.dailyResetAtUtc} />}
      >
        <div className="space-y-2">
          {data.daily.map((q) => (
            <QuestCard
              key={q.id}
              quest={q}
              onClaim={() => onClaim(q.id)}
              claiming={claim.isPending}
              justClaimed={recentClaim === q.id}
              cardRef={setItemRef(q.id)}
            />
          ))}
        </div>
      </Section>

      {allOnboardingDone && (
        <div className="rounded-xl bg-tg-secondaryBg p-3 text-center text-sm text-tg-hint">
          Туториал пройден полностью. Возвращайся каждый день за ежедневной
          порцией наград.
        </div>
      )}
    </div>
  );
}

function ChestCard({
  chest,
  onClaim,
  claiming,
  justClaimed,
  cardRef,
}: {
  chest: DailyChest;
  onClaim: () => void;
  claiming: boolean;
  justClaimed?: boolean;
  cardRef?: (el: HTMLElement | null) => void;
}) {
  const Icon = chest.claimable ? GiOpenChest : GiLockedChest;
  // Colour theme echoes Bronze / Silver / Gold tiers from the catalog.
  const palette =
    chest.order === 1
      ? { ring: "ring-amber-700/50", text: "text-amber-500" }
      : chest.order === 2
        ? { ring: "ring-slate-300/40", text: "text-slate-300" }
        : { ring: "ring-yellow-400/60", text: "text-yellow-400" };

  const state = chest.claimed
    ? "claimed"
    : chest.claimable
      ? "claimable"
      : "locked";

  return (
    <button
      ref={cardRef}
      type="button"
      disabled={!chest.claimable || claiming}
      onClick={onClaim}
      className={`flex flex-col items-center gap-1 rounded-xl bg-tg-secondaryBg p-2 ring-1 transition-all ${palette.ring} ${
        state === "claimable" ? "animate-element-pulse" : ""
      } ${state === "claimed" ? "opacity-40" : ""} ${
        !chest.claimable && !chest.claimed ? "opacity-70" : ""
      } ${justClaimed ? "animate-celebrate-ring" : ""}`}
    >
      <Icon size={36} className={palette.text} />
      <div className="text-[10px] text-tg-hint">
        {chest.completedQuests} / {chest.requiredQuests}
      </div>
      <div className="text-xs font-medium leading-tight">{chest.title}</div>
      <RewardChip reward={chest.reward} />
      <div className="text-[10px] text-tg-hint">
        {state === "claimed"
          ? "Получено"
          : state === "claimable"
            ? "Открыть!"
            : `Заверши ${chest.requiredQuests}`}
      </div>
    </button>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs uppercase tracking-wide text-tg-hint">
        <span>{title}</span>
        {subtitle && <span className="text-tg-accent">{subtitle}</span>}
      </div>
      {children}
    </section>
  );
}

function QuestCard({
  quest,
  onClaim,
  claiming,
  justClaimed,
  cardRef,
}: {
  quest: QuestEntry;
  onClaim: () => void;
  claiming: boolean;
  justClaimed?: boolean;
  cardRef?: (el: HTMLElement | null) => void;
}) {
  const pct = quest.target === 0 ? 0 : Math.min(100, Math.round((quest.progress / quest.target) * 100));
  const stateClass = quest.claimed
    ? "opacity-60"
    : quest.claimable
    ? "ring-1 ring-tg-accent"
    : "";

  return (
    <div
      ref={cardRef as (el: HTMLDivElement | null) => void}
      className={`relative rounded-xl bg-tg-secondaryBg p-3 ${stateClass} ${
        justClaimed ? "animate-celebrate-ring" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium leading-tight">{quest.title}</div>
          <div className="mt-0.5 text-xs text-tg-hint leading-snug">
            {quest.hint}
          </div>
        </div>
        <RewardChip reward={quest.reward} />
      </div>

      <div className="mt-2">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-tg-hint tabular-nums">
            {quest.progress} / {quest.target}
          </span>
          <span className="text-tg-hint tabular-nums">{pct}%</span>
        </div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-tg-bg">
          <div
            className={`h-full rounded-full transition-all ${quest.claimed ? "bg-tg-hint/30" : "bg-tg-button"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {quest.claimed ? (
        <div className="mt-2 text-center text-xs text-tg-hint">Награда получена</div>
      ) : (
        <button
          type="button"
          disabled={!quest.claimable || claiming}
          onClick={onClaim}
          className="mt-2 w-full rounded-lg bg-tg-button px-3 py-2 text-sm font-medium text-tg-buttonText disabled:opacity-40"
        >
          {quest.claimable ? "Получить награду" : "В процессе"}
        </button>
      )}
    </div>
  );
}

function RewardChip({ reward }: { reward: QuestReward }) {
  // Compact icon row of the reward. Only show non-zero fields.
  const items: { kind: "gold" | "wood" | "stone" | "mana" | "xp" | "shards"; v: number }[] = [];
  if (reward.gold > 0) items.push({ kind: "gold", v: reward.gold });
  if (reward.wood > 0) items.push({ kind: "wood", v: reward.wood });
  if (reward.stone > 0) items.push({ kind: "stone", v: reward.stone });
  if (reward.mana > 0) items.push({ kind: "mana", v: reward.mana });
  if (reward.heroXp > 0) items.push({ kind: "xp", v: reward.heroXp });
  if (reward.soulShards > 0) items.push({ kind: "shards", v: reward.soulShards });

  const crystals: { art: string; alt: string; n: number }[] = [];
  if ((reward.dimCrystals ?? 0) > 0)
    crystals.push({ art: "/icons/crystals/dim.png", alt: "Тусклый кристалл", n: reward.dimCrystals! });
  if ((reward.shiningCrystals ?? 0) > 0)
    crystals.push({ art: "/icons/crystals/shining.png", alt: "Сияющий кристалл", n: reward.shiningCrystals! });
  if ((reward.ancientCrystals ?? 0) > 0)
    crystals.push({ art: "/icons/crystals/ancient.png", alt: "Древний кристалл", n: reward.ancientCrystals! });

  return (
    <div className="shrink-0 rounded-lg bg-tg-bg px-2 py-1 text-xs">
      <div className="flex flex-wrap items-center gap-1.5">
        {items.map((i) => (
          <CurrencyValue key={i.kind} kind={i.kind} value={formatNumber(i.v)} size={12} />
        ))}
        {crystals.map((c) => (
          <span key={c.alt} className="inline-flex items-center gap-0.5">
            <img
              src={c.art}
              alt={c.alt}
              className="h-4 w-auto object-contain"
              loading="lazy"
              draggable={false}
            />
            <span className="tabular-nums">×{c.n}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function NewbieChestCard({
  state,
  onClaim,
  claiming,
  justClaimed,
  cardRef,
}: {
  state: NewbieChestState;
  onClaim: () => void;
  claiming: boolean;
  justClaimed?: boolean;
  cardRef?: (el: HTMLElement | null) => void;
}) {
  const now = useNow(1000);
  const ready = state.available && secondsUntil(state.nextAvailableAt, now) <= 0;
  const seconds = secondsUntil(state.nextAvailableAt, now);
  const Icon = ready ? GiOpenChest : GiLockedChest;
  const reward: QuestReward = {
    gold: state.reward.gold,
    wood: state.reward.wood,
    stone: state.reward.stone,
    mana: state.reward.mana,
    heroXp: 0,
    soulShards: 0,
  };
  const items = Object.entries(state.reward.items ?? {}).filter(([, c]) => c > 0);

  return (
    <button
      ref={cardRef}
      type="button"
      disabled={!ready || claiming}
      onClick={onClaim}
      className={`flex w-full items-center gap-3 rounded-xl bg-tg-secondaryBg p-3 ring-1 transition-all ${
        ready
          ? "ring-amber-400/60 animate-element-pulse"
          : "ring-tg-secondaryBg opacity-70"
      } ${justClaimed ? "animate-celebrate-ring" : ""}`}
    >
      <Icon size={44} className={ready ? "text-amber-400" : "text-tg-hint"} />
      <div className="flex flex-1 flex-col items-start gap-1 text-left">
        <div className="text-sm font-medium">
          {ready ? "Сундук готов — забирай" : "Следующий через"}
        </div>
        <div className="text-xs text-tg-hint">
          {ready
            ? "Возвращайся каждые 10 минут — ресурсы для роста"
            : formatDuration(seconds)}
        </div>
        <RewardChip reward={reward} />
        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-tg-bg px-2 py-1 text-xs">
            {items.map(([id, count]) => (
              <span key={id} className="inline-flex items-center gap-1">
                <span aria-hidden>+</span>
                <span>
                  {speedupLabel(id)} {count > 1 ? `×${count}` : ""}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
      {ready && <GiTreasureMap size={22} className="text-tg-hint" />}
    </button>
  );
}

function speedupLabel(itemId: string): string {
  switch (itemId) {
    case "spd-1m":  return "+1 мин";
    case "spd-5m":  return "+5 мин";
    case "spd-30m": return "+30 мин";
    case "spd-1h":  return "+1 час";
    case "spd-8h":  return "+8 часов";
    case "spd-24h": return "+24 часа";
    default:        return itemId;
  }
}

function ResetCountdown({ resetAtUtc }: { resetAtUtc: string }) {
  const target = useMemo(() => new Date(resetAtUtc).getTime(), [resetAtUtc]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const msLeft = Math.max(0, target - now);
  const hours = Math.floor(msLeft / 3_600_000);
  const mins = Math.floor((msLeft % 3_600_000) / 60_000);
  return <>сброс через {hours}ч {mins}м</>;
}
